/* «Свой аватар» — мастер нового варианта «Вы» (APP-SPEC-5 §10). Шапка «Свой аватар · Шаг N из 3 · …» со степпером,
   сцена постоянной высоты (циферблат фото на шаге 1, лицо варианта на шаге 2), шаги «Фото» → «Имя и стиль» →
   «Обработка» и «Готово» (хвост заказа). Поля — только опубликованные формой (photo_NN, variant_name, base,
   consent_version, consent_accepted, по наличию — voice_id); проверки снимков — ответ сервера загрузки. */
import { useEffect, useState } from 'react';
import { api, endpoints } from '../../api';
import type { UserAvatar } from '../../api/people';
import type { UserVoice } from '../../api/user-voices';
import { uiFormat, uiText } from '../../i18n';
import { shake } from '../../ui/motion';
import type { StudioApp } from '../studio/studio-api';
import { record, rows, text } from '../studio/studio-content';
import { openStudioTarget } from '../studio/studio-navigation';
import { StudioIcon } from '../studio/StudioIcon';
import { StudioPhotoTray } from '../studio/StudioPhotoTray';
import { StudioSheet } from '../studio/StudioSheet';
import { useStudioDocument } from '../studio/use-studio-document';
import { useStudioForm } from '../studio/use-studio-form';
import { useStudioPhotoPreviews } from '../studio/use-studio-photo-previews';
import { CreateFoot, RightsBox } from '../shoot/kit/FormParts';
import { fieldOf, firstField, photoSlots, plural } from '../shoot/kit/form-model';
import { avatarVoicePicked, avatarVoiceValue } from './avatar-voice';
import { OrderTail } from '../shoot/kit/OrderTail';
import { checkLabel, checkOk, useUploadChecks } from '../shoot/kit/use-upload-checks';
import './avatar.css';
import './native-avatar.css';

const BASES: Record<string, [string, string]> = {
  real: ['Реалистичный', 'Как на фото'],
  illus: ['Иллюстрация', 'Рисованный'],
  '3d': ['3D', 'Объёмный'],
};
const STEP_NAMES = ['Фото', 'Имя и стиль', 'Обработка'];
const photosWord = (count: number) => plural(count, uiText('фото'), uiText('фото'), uiText('фото'));

function Dial({ ready, max, min }: { ready: number; max: number; min: number }) {
  return (
    <svg className="native-avatar-dial" viewBox="0 0 200 200" aria-hidden="true">
      {Array.from({ length: max }, (_, index) => (
        <circle
          key={index}
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke={index < ready ? 'var(--fg)' : 'var(--line2)'}
          strokeWidth="6"
          strokeLinecap="round"
          pathLength={max}
          strokeDasharray={`0.8 ${max}`}
          transform={`rotate(${-90 + (index * 360) / max} 100 100)`}
        />
      ))}
      {[min, 10, 15]
        .filter(mark => mark > 0 && mark < max)
        .map(mark => {
          const angle = ((mark / max) * 360 - 90) * (Math.PI / 180);
          return <circle key={mark} cx={100 + 78 * Math.cos(angle)} cy={100 + 78 * Math.sin(angle)} r="2" fill="var(--fg3)" />;
        })}
    </svg>
  );
}

export function AvatarOrderForm({
  app,
  avatar,
  initialInputs,
  registerBack,
}: {
  app: StudioApp;
  avatar: UserAvatar | null;
  initialInputs?: Record<string, unknown>;
  registerBack?(handler: () => boolean): () => void;
}) {
  const state = useStudioForm({ app, avatar, initialInputs });
  const { form, inputs, locked } = state;
  const screenDoc = useStudioDocument(signal => api.request<Record<string, unknown>>('/app/studio/screens/avatar', { signal }), 'avatar-screen');
  const data = record(screenDoc.data);
  const [step, setStep] = useState(1);
  const [sheet, setSheet] = useState<'tips' | 'voice' | { key: string } | null>(null);
  const [hint, setHint] = useState('');
  const [balance, setBalance] = useState<number | null>(null);
  const [voices, setVoices] = useState<UserVoice[] | null>(null);
  const [self, setSelf] = useState<Array<{ upload_id: string; url: string }>>([]);
  const slots = photoSlots(form);
  const previews = useStudioPhotoPreviews(state, slots);
  const filled = slots.filter(slot => !!inputs[slot.key]);
  const checks = useUploadChecks(Object.fromEntries(filled.map(slot => [slot.key, inputs[slot.key]])));
  const good = filled.filter(slot => checkOk(checks[slot.key]));
  const bad = filled.filter(slot => !checkOk(checks[slot.key]));
  const minimum = form?.constraints?.min_photos ?? (Number(data.min_photos) || 5);
  const maximum = form?.constraints?.max_photos ?? (Number(data.max_photos) || slots.length || 20);
  const nameField = fieldOf(form, 'variant_name');
  const nameLimit = Math.min(form?.constraints?.name_max_unicode_codepoints ?? 24, nameField?.max_length ?? 24);
  const voiceField = firstField(form, ['voice_id', 'voice']);
  const pickedVoice = voiceField ? avatarVoicePicked(voiceField, inputs[voiceField.key]) : '';
  const baseField = fieldOf(form, 'base');
  const bases = (baseField?.values ?? []).filter(value => value in BASES);
  const hints = Array.isArray(data.name_hints) ? data.name_hints.filter((item): item is string => typeof item === 'string') : ['Деловой', 'Повседневный', 'Праздничный'];
  const tips = record(data.tips);
  const shownStep = state.orderId ? 3 : step;
  const terminal = state.order?.status;
  const name = (inputs.variant_name ?? '').trim();
  const price = form?.expected_price ?? 0;
  const first = good.map(slot => previews.urls[slot.key]).find(Boolean) ?? filled.map(slot => previews.urls[slot.key]).find(Boolean);
  const set = (key: string, value: string | undefined) =>
    state.setInputs(current => {
      const next = { ...current };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });

  useEffect(() => {
    if (shownStep !== 2 || !registerBack) return;
    return registerBack(() => {
      setStep(1);
      return true;
    });
  }, [shownStep, registerBack]);
  useEffect(() => {
    let active = true;
    endpoints
      .balance()
      .then(value => active && typeof value.circle === 'number' && setBalance(value.circle))
      .catch(() => {});
    endpoints.people
      .self()
      .then(value => active && setSelf(value.photos))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    if (sheet !== 'voice' || voices) return;
    let active = true;
    endpoints.userVoices
      .list()
      .then(page => active && setVoices(page.items.filter(item => item.status !== 'failed')))
      .catch(() => active && setVoices([]));
    return () => {
      active = false;
    };
  }, [sheet, voices]);

  /* «Вы» уже в аккаунте: его снимки встают в свободные слоты (только по явному нажатию — их снова проверит сервер) */
  const freeSlots = slots.filter(slot => !inputs[slot.key] && !state.uploadIds[slot.key]);
  const fromSelf = self.filter(photo => !Object.values(inputs).includes(photo.upload_id));
  const importSelf = () => {
    if (!state.rights) return setHint(uiText('Сначала отметьте согласие ниже'));
    const pairs = freeSlots.slice(0, fromSelf.length).map((slot, index) => [slot.key, fromSelf[index].upload_id] as const);
    state.setInputs(current => ({ ...current, ...Object.fromEntries(pairs) }));
    state.setUploadIds(current => ({ ...current, ...Object.fromEntries(pairs) }));
  };

  const dialNote =
    good.length < minimum
      ? good.length
        ? uiFormat('Ещё {0} до минимума', minimum - good.length)
        : uiFormat('Нужно от {0}', minimum)
      : good.length < 10
        ? uiText('Можно дальше')
        : uiText('Хорошо');
  const stepOneMissing = good.length < minimum ? uiFormat('Нужно от {0} подходящих фото', minimum) : null;
  const short = balance !== null && form?.chargeable ? price - balance : 0;
  const stepTwoMissing = !name
    ? 'Укажите имя варианта'
    : Array.from(name).length > nameLimit
      ? 'Сократите имя варианта'
      : baseField && !inputs.base
        ? 'Выберите стиль'
        : !state.rights || inputs.consent_accepted !== 'true'
          ? 'Отметьте согласие'
          : stepOneMissing;
  const tapStep = (index: number, target: HTMLElement) => {
    const refuse = (message: string) => {
      shake(target);
      setHint(uiText(message));
    };
    if (state.orderId) return refuse('Шаги закрыты: вариант уже создаётся. Новый вариант — после итога');
    if (index === 1) return setStep(1);
    if (index === 2) return stepOneMissing ? refuse(stepOneMissing) : setStep(2);
    refuse('Шаг 3 начнётся после «Создать вариант»');
  };

  return (
    <section className="native-studio-form native-avatar av-in">
      <div className="native-avatar-head bc-sticky">
        <h2>{uiText('Свой аватар')}</h2>
        <small>
          {uiFormat('Шаг {0} из 3', shownStep)} ·{' '}
          {uiText(
            shownStep === 3
              ? terminal === 'failed'
                ? 'Не получилось'
                : terminal === 'done'
                  ? 'Готово'
                  : STEP_NAMES[2]
              : STEP_NAMES[shownStep - 1],
          )}
        </small>
        <div className="native-avatar-steps" role="group" aria-label={uiText('Шаги создания варианта')}>
          {[1, 2, 3].map(index => (
            <button
              type="button"
              key={index}
              aria-label={uiFormat('Шаг {0}: {1}', index, uiText(STEP_NAMES[index - 1]))}
              aria-current={shownStep === index ? 'step' : undefined}
              onClick={event => tapStep(index, event.currentTarget)}
            >
              <span className={shownStep >= index ? 'on' : ''} />
            </button>
          ))}
        </div>
      </div>
      {hint && (
        <p className="native-avatar-note" role="status">
          {hint}
        </p>
      )}
      {!form && !state.error && <p role="status">{uiText('Загружаем…')}</p>}
      {state.error && (
        <p role="alert">
          {uiText(state.error)}{' '}
          <button type="button" disabled={state.busy} onClick={() => state.setRevision(value => value + 1)}>
            {uiText('Повторить')}
          </button>
        </p>
      )}
      {form && !state.orderId && (
        <form
          onSubmit={event => {
            event.preventDefault();
            if (step === 1) {
              if (!stepOneMissing) setStep(2);
            } else if (!state.busy && !state.submissionBlocked && (state.intent || (!stepTwoMissing && form.available && short <= 0))) void state.submit();
          }}
        >
          <div className="native-avatar-stage">
            <div className={`native-avatar-layer${step === 1 ? ' on' : ''}`} aria-hidden={step !== 1}>
              <Dial ready={good.length} max={maximum} min={minimum} />
              <b>{uiFormat('{0} из {1} фото', good.length, maximum)}</b>
              <small>{dialNote}</small>
            </div>
            <div className={`native-avatar-layer face${step === 2 ? ' on' : ''}`} aria-hidden={step !== 2}>
              {first ? <img className={`base-${inputs.base || 'real'}`} src={first} alt={uiText('Лицо варианта')} /> : <span>{uiText('Ваш вариант')}</span>}
            </div>
          </div>
          <div className="native-avatar-title">
            <h3>{step === 1 ? uiText('Новый вариант «Вы»') : `${uiText('Вы')} · ${name || uiText('Новый вариант')}`}</h3>
            <p>
              {step === 1
                ? uiFormat('Из ваших фото: от {0} до {1} снимков, где хорошо видно лицо', minimum, maximum)
                : [uiText(BASES[inputs.base]?.[0] ?? 'Выберите стиль'), photosWord(good.length), voiceField ? (pickedVoice ? uiText('с голосом') : uiText('без голоса')) : '']
                    .filter(Boolean)
                    .join(' · ')}
            </p>
          </div>
          {step === 1 ? (
            <>
              <button type="button" className="btn line" onClick={() => setSheet('tips')}>
                {uiText('Какие фото подойдут')}
              </button>
              {!!fromSelf.length && !!freeSlots.length && (
                <button type="button" className="btn line" disabled={locked} onClick={importSelf}>
                  {uiFormat('Взять фото из «Вы» · {0}', Math.min(fromSelf.length, freeSlots.length))}
                </button>
              )}
              <StudioPhotoTray
                state={state}
                slots={slots}
                urls={previews.urls}
                minimum={minimum}
                title="Снимки"
                description="Загрузить фото · Сразу несколько · Снять · Селфи с камеры"
                consent={<RightsBox checked={state.rights} disabled={locked} text="Подтверждаю право использовать эти фотографии" onChange={state.setRights} />}
              />
              {!filled.length && (
                <div className="native-avatar-examples">
                  <div>
                    <b>{uiText('Подойдут')}</b>
                    <p>{uiText('Крупно · Разный свет · Ракурсы · Эмоции')}</p>
                  </div>
                  <div>
                    <b>{uiText('Не подойдут')}</b>
                    <p>{uiText('Очки · Двое · Размыто · Фильтры')}</p>
                  </div>
                </div>
              )}
              {!!filled.length && (
                <div className="native-avatar-checks" role="list" aria-label={uiText('Проверка снимков')}>
                  {filled.map((slot, index) => (
                    <button type="button" role="listitem" key={slot.key} className={checkOk(checks[slot.key]) ? 'ok' : 'bad'} onClick={() => setSheet({ key: slot.key })}>
                      {previews.urls[slot.key] ? <img src={previews.urls[slot.key]} alt={uiFormat('Фото {0}', index + 1)} /> : <span className="ph" />}
                      <small>{checkOk(checks[slot.key]) ? (checks[slot.key] ? uiText('Подходит') : uiText('Проверяем')) : uiText(checkLabel(checks[slot.key]))}</small>
                    </button>
                  ))}
                </div>
              )}
              {!!bad.length && (
                <p className="note warn">
                  {uiFormat('{0} не подойдут и не войдут в обучение. Нажмите на снимок, чтобы заменить', photosWord(bad.length))}
                </p>
              )}
              {previews.error && (
                <p role="alert">
                  {uiText('Не удалось открыть некоторые фото.')}{' '}
                  <button type="button" onClick={previews.retry}>
                    {uiText('Повторить')}
                  </button>
                </p>
              )}
            </>
          ) : (
            <>
              {nameField && (
                <div className="native-avatar-name">
                  <label>
                    <span>{uiText('Имя варианта')}</span>
                    <input
                      value={inputs.variant_name ?? ''}
                      disabled={locked}
                      maxLength={nameLimit * 2}
                      onChange={event => set('variant_name', Array.from(event.target.value).slice(0, nameLimit).join(''))}
                    />
                    <small className="mono">
                      {Array.from(inputs.variant_name ?? '').length} / {nameLimit}
                    </small>
                  </label>
                  <div className="chips">
                    {hints.map(value => (
                      <button type="button" key={value} className={`chip${name === value ? ' on' : ''}`} disabled={locked} onClick={() => set('variant_name', value)}>
                        {uiText(value)}
                      </button>
                    ))}
                  </div>
                  <p className="native-avatar-note">{uiFormat('В ряду «Кто в кадре» и в каталоге он будет «Вы · {0}»', name || uiText('…'))}</p>
                </div>
              )}
              {!!bases.length && (
                <div className="native-avatar-bases" role="radiogroup" aria-label={uiText('Стиль')}>
                  {bases.map(value => (
                    <button
                      type="button"
                      role="radio"
                      key={value}
                      aria-checked={inputs.base === value}
                      className={inputs.base === value ? 'on' : ''}
                      disabled={locked}
                      onClick={() => set('base', value)}
                    >
                      <span className="pc">{first ? <img className={`base-${value}`} src={first} alt="" /> : <StudioIcon kind="face" />}</span>
                      <b>{uiText(BASES[value][0])}</b>
                      <small>{uiText(text(record(rows(data.bases).find(item => item.id === value)).sub) || BASES[value][1])}</small>
                    </button>
                  ))}
                </div>
              )}
              {voiceField && (
                <button type="button" className="sc-r" disabled={locked} onClick={() => setSheet('voice')}>
                  <span className="sc-th">
                    <StudioIcon kind="voice" />
                  </span>
                  <span className="m">
                    <b>{uiText('Голос')}</b>
                    <small>
                      {pickedVoice
                        ? voices?.find(item => String(item.voice_id) === pickedVoice)?.name ?? uiText('Выбран')
                        : uiText('По желанию: голос можно выбрать и у каждого кружка')}
                    </small>
                  </span>
                  <StudioIcon kind="chev" />
                </button>
              )}
              <label className="photo-consent">
                <input
                  type="checkbox"
                  checked={inputs.consent_accepted === 'true'}
                  disabled={locked}
                  onChange={event => {
                    state.setRights(event.target.checked);
                    set('consent_accepted', event.target.checked ? 'true' : undefined);
                  }}
                />
                {uiText('На фото я или у меня есть согласие')}
              </label>
              <p className="bc-cap native-avatar-note">{uiText('Дети, публичные люди и фото без согласия не пройдут проверку, Circle не вернутся')}</p>
              <p className="native-avatar-note">{uiText('Вариант видите и используете только вы. Удалить его можно в «Профиле»')}</p>
              {form.chargeable && (
                <p className="native-avatar-note">
                  {uiFormat('Спишем {0} C один раз', price)}
                  {balance !== null ? ` · ${uiFormat('у вас {0} C', balance.toLocaleString('ru'))}` : ''}
                </p>
              )}
            </>
          )}
          {step === 1 ? (
            <CreateFoot label="Дальше" busy={false} blocked={false} pending={false} missing={stepOneMissing} available onCreate={() => setStep(2)} />
          ) : short > 0 && !state.intent ? (
            <CreateFoot
              label={uiFormat('Не хватает {0} C · Пополнить', short)}
              busy={false}
              blocked={false}
              pending={false}
              missing={null}
              available
              onCreate={() => {
                location.hash = '#balance';
              }}
            />
          ) : (
            <CreateFoot
              label={form.chargeable ? uiFormat('Создать вариант · {0} C', price) : 'Создать вариант'}
              busy={state.busy}
              blocked={state.submissionBlocked}
              pending={!!state.intent}
              missing={stepTwoMissing}
              available={form.available}
              onCreate={() => void state.submit()}
            />
          )}
        </form>
      )}
      {sheet === 'tips' && (
        <StudioSheet title="Какие фото подойдут" onClose={() => setSheet(null)}>
          <h2>{uiText(text(tips.title) || 'Чем разнообразнее снимки, тем живее вариант')}</h2>
          <h3>{uiText('Подойдут')}</h3>
          <p>{uiText('Крупно · Разный свет · Ракурсы · Эмоции')}</p>
          <h3>{uiText('Не подойдут')}</h3>
          <p>{uiText('Очки · Двое · Размыто · Фильтры')}</p>
          <ul>
            <li>{uiText('Один и тот же человек на всех снимках')}</li>
            <li>{uiText('Лицо видно целиком, без очков и масок')}</li>
            <li>{uiText('Разные снимки, без повторов')}</li>
          </ul>
          <button type="button" className="btn" onClick={() => setSheet(null)}>
            {uiText('Понятно')}
          </button>
        </StudioSheet>
      )}
      {sheet && typeof sheet === 'object' && (
        <StudioSheet title="Снимок" onClose={() => setSheet(null)}>
          {(() => {
            const index = filled.findIndex(slot => slot.key === sheet.key);
            const check = checks[sheet.key];
            return (
              <>
                <h2>
                  {uiFormat('Фото {0}', index + 1)} · {checkOk(check) ? uiText('Подойдёт') : uiFormat('Не подойдёт: {0}', checkLabel(check).toLocaleLowerCase())}
                </h2>
                {previews.urls[sheet.key] && <img className="native-avatar-big" src={previews.urls[sheet.key]} alt="" />}
                <ul className="native-avatar-checklist">
                  {(check?.checks ?? []).map(item => (
                    <li key={item.key} className={item.ok ? 'ok' : 'bad'}>
                      {item.ok ? '✓' : '×'} {uiText(checkLabel({ ...check!, checks: [{ ...item, ok: false }] }) || item.key)}
                    </li>
                  ))}
                  {check?.warnings.map(item => <li key={item}>{item}</li>)}
                </ul>
                <button
                  type="button"
                  className="btn line"
                  disabled={locked}
                  onClick={() => {
                    state.clearUpload(sheet.key);
                    setSheet(null);
                  }}
                >
                  {uiText('Убрать')}
                </button>
                <p className="native-avatar-note">{uiText('Чтобы заменить, уберите снимок и загрузите другой')}</p>
              </>
            );
          })()}
        </StudioSheet>
      )}
      {sheet === 'voice' && voiceField && (
        <StudioSheet title="Голос варианта" onClose={() => setSheet(null)}>
          <h2>{uiText('Голос варианта')}</h2>
          <p>{uiText('По желанию: им вариант говорит в кружках')}</p>
          <h3>{uiText('Ваш голос')}</h3>
          {voices === null && <p role="status">{uiText('Загружаем…')}</p>}
          {voices?.length === 0 && (
            <button type="button" className="sc-r" onClick={() => (location.hash = '#clone')}>
              <span className="m">
                <b>{uiText('Записать свой голос')}</b>
                <small>{uiText('Раздел «Клон»: от 2 секунд речи')}</small>
              </span>
            </button>
          )}
          {voices?.map(item => (
            <button
              type="button"
              key={item.id}
              className={`sc-r${pickedVoice === String(item.voice_id) ? ' on' : ''}`}
              aria-pressed={pickedVoice === String(item.voice_id)}
              disabled={locked || item.status !== 'ready' || !item.voice_id}
              onClick={() => {
                if (item.voice_id) set(voiceField.key, avatarVoiceValue(voiceField, item.voice_id));
                setSheet(null);
              }}
            >
              <span className="m">
                <b>{item.name}</b>
                <small>{item.status === 'ready' ? uiText('Ваш клон') : uiText('Ещё учится, скоро будет готов')}</small>
              </span>
            </button>
          ))}
          <h3>{uiText('Другой голос')}</h3>
          <button
            type="button"
            className={`sc-r${!pickedVoice ? ' on' : ''}`}
            onClick={() => {
              set(voiceField.key, undefined);
              setSheet(null);
            }}
          >
            <span className="m">
              <b>{uiText('Позже')}</b>
              <small>{uiText('Голос можно выбрать у каждого кружка')}</small>
            </span>
          </button>
        </StudioSheet>
      )}
      <OrderTail
        state={state}
        kind="avatar"
        groupLabel={name ? `${uiText('Вы')} · ${name}` : uiText('Вариант')}
        hero={first ? <img className={`native-avatar-hero base-${inputs.base || 'real'}`} src={first} alt={uiText('Лицо варианта')} /> : undefined}
        copy={{
          running: ['Создаём вариант', 'Обычно около 10 минут. Можно закрыть: бот пришлёт вариант в чат, когда он будет готов.'],
          done: ['Вариант готов', uiFormat('«Вы · {0}» уже выбран за «Вы»: он в «Кто в кадре» на всех экранах, в каталоге персонажей и в «Профиле».', name)],
          failed: ['Вариант не создан', 'На снимках публичный человек или человек без согласия: такие варианты не делаем.'],
        }}
        card={[uiText(BASES[inputs.base]?.[0] ?? ''), photosWord(good.length), voiceField ? (pickedVoice ? uiText('с голосом') : uiText('без голоса')) : '']
          .filter(Boolean)
          .join(' · ')}
        saveLabel={null}
        again={null}
        failLabel="Заменить фото"
        primary={{ label: 'Сделать кружок', onClick: () => openStudioTarget({ screen: 'circle' }) }}
        next={[
          { label: 'Фото', icon: 'photo', onClick: () => openStudioTarget({ screen: 'photo', options: { mode: 'text' } }) },
          { label: 'Видео', icon: 'video', onClick: () => openStudioTarget({ screen: 'video', options: { mode: 'text' } }) },
        ]}
      />
    </section>
  );
}
